

import React, { useState, useEffect } from 'react';
import { Button, Modal, Spinner, Table, Toast } from 'flowbite-react';
import get from 'lodash/get';
import { useMutation } from 'react-query';
import { addDriveRooms, softDeleteDriveRoom } from 'src/apis/offlineDrive';
import { addDriveRoomsInput } from 'types';
import { useToast } from 'src/context/ToastProvider';
import { MdDelete } from 'react-icons/md';


type DriveRoomData = addDriveRoomsInput & { _id?: string };

type Props = {
  isOpen: boolean;
  onClose: () => void;
  data: DriveRoomData[];
  header?: string;
  isLoading?: boolean;
};
const ShowDriveRoomModal: React.FC<Props> = ({ isOpen, onClose, data, header, isLoading }) => {
  const toast = useToast();
  const [roomsData, setRoomsData] = useState<DriveRoomData[]>(data);

  useEffect(() => {
    setRoomsData(data);
  }, [data]);

  const addDriveRoomsFn = useMutation(['addDriveRooms'], addDriveRooms, {
    onSuccess(data) {
      console.log("onSuccess", data);
      toast({
        title: 'Drive Rooms added successfully',
        type: 'success',
      });
      onClose();

    },
    onError(error: Error) {
      const errorMessage = get(error, ['response', 'data', 'message'], error.message);
      toast({
        title: errorMessage,
        type: 'error',
      });
      onClose();
    },
  });

  const softDeleteDriveRoomFn = useMutation(['softDeleteDriveRoom'], softDeleteDriveRoom, {
    onSuccess() {
      toast({
        title: 'Drive Room deleted successfully',
        type: 'success',
      });
    },
    onError(error: Error) {
      const errorMessage = get(error, ['response', 'data', 'message'], error.message);
      toast({
        title: errorMessage,
        type: 'error',
      });
    },
  });

  const handleDelete = async (room: DriveRoomData, index: number) => {
    if (room._id) {
      // Existing room - soft delete via API
      try {
        await softDeleteDriveRoomFn.mutateAsync(room._id);
        // Remove from local state after successful API call
        setRoomsData(prev => prev.filter((_, i) => i !== index));
      } catch (error) {
        // Error handling is done in mutation
      }
    } else {
      // New room - just remove from local state
      setRoomsData(prev => prev.filter((_, i) => i !== index));
      toast({
        title: 'Room removed from list',
        type: 'success',
      });
    }
  };

  const onSubmit = () => {
    console.log('onSubmit');
    // Only submit rooms that are not archived (new rooms without _id)
    const newRooms = roomsData.filter(room => !room._id);
    if (newRooms.length > 0) {
      addDriveRoomsFn.mutate(newRooms as addDriveRoomsInput[]);
    } else {
      toast({
        title: 'No new rooms to add',
        type: 'info',
      });
      onClose();
    }
  };

  if (!isOpen) return <div />;
  const keys = ['Sr No', 'City', 'Building Name', 'Floor Name', 'Room Name', 'Actions'];
  return (
    <Modal dismissible={true} show={isOpen} onClose={onClose} size="6xl">
      <Modal.Header className="bg-blue-50">{header || 'Drive Rooms'}</Modal.Header>
      <Modal.Body>
        <div className="max-w-full whitespace-pre grid grid-cols-1 overflow-auto">
          <Table striped={true}>
            <Table.Head>
              {keys.map((keyName, index) => (
                <Table.HeadCell key={index} className="uppercase">
                  {keyName}
                </Table.HeadCell>
              ))}
            </Table.Head>
            <Table.Body>
              {roomsData.map((room, index) => (
                <Table.Row key={room._id || index}>
                  <Table.Cell>{index + 1}</Table.Cell>
                  <Table.Cell>{room.city}</Table.Cell>
                  <Table.Cell>{room.buildingName}</Table.Cell>
                  <Table.Cell>{room.floorName}</Table.Cell>
                  <Table.Cell>{room.roomName}</Table.Cell>
                  <Table.Cell>
                    <Button
                      color="failure"
                      size="xs"
                      onClick={() => handleDelete(room, index)}
                      disabled={softDeleteDriveRoomFn.isLoading || isLoading}
                    >
                      <MdDelete className="mr-1" />
                      Delete
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="w-full flex flex-row-reverse">
          <Button className="w-[145px]" color="success" onClick={onSubmit} disabled={isLoading || addDriveRoomsFn.isLoading}>
            {addDriveRoomsFn.isLoading ? <Spinner size="sm" /> : 'Confirm'}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ShowDriveRoomModal;
